import { useContext, useEffect, useState, useCallback } from "react";
import { OrgContext } from "contexts/OrgContext";
import { ExternalNodeService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/external_node_connect";
import {
  PortProtocol,
  HttpPortProtocol,
  NetworkMemberType,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { getSSHPortId } from "components/NodeDetail/Environment/TabContent/Access/SSHAccess/addSSHAccessToEnvironmentRequest";
import { buildSecureLinkCustomHostname } from "components/Environment/utils/secureLinkHostname";
import { createConnectClient } from "server/connect";

const client = createConnectClient(ExternalNodeService);

type ExternalNode = NonNullable<
  Awaited<ReturnType<typeof client.getNode>>["externalNode"]
>;

export type { ExternalNode, PortProtocol, HttpPortProtocol };

export interface OpenHTTPPortOptions {
  httpProtocol: HttpPortProtocol;
  customHostname?: string;
  authorizedEmails: string[];
  allowPublicUnauthenticated: boolean;
}

export const useNodeDetail = (nodeId: string) => {
  const { activeOrgId } = useContext(OrgContext);
  const [node, setNode] = useState<ExternalNode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<unknown>(null);

  const fetchNode = useCallback(
    async (showSpinner = false) => {
      if (!activeOrgId || !nodeId) return;
      if (showSpinner) setIsLoading(true);
      setFetchError(null);
      try {
        const res = await client.getNode({
          externalNodeId: nodeId,
          organizationId: activeOrgId,
        });
        setNode(res.externalNode ?? null);
      } catch (e) {
        setFetchError(e);
      } finally {
        if (showSpinner) setIsLoading(false);
      }
    },
    [nodeId, activeOrgId],
  );

  useEffect(() => {
    fetchNode(true);
  }, [fetchNode]);

  const openPort = useCallback(
    async (
      portNumber: number,
      protocol: PortProtocol,
      allowedSources: string[],
      toPortNumber?: number,
    ) => {
      if (toPortNumber === undefined) {
        await client.openPort({
          externalNodeId: nodeId,
          portNumber,
          protocol,
          allowedSources,
        });
      } else {
        await client.openSequentialPorts({
          externalNodeId: nodeId,
          fromPortNumber: portNumber,
          toPortNumber,
          protocol,
          allowedSources,
        });
      }
      await fetchNode();
    },
    [nodeId, fetchNode],
  );

  const openHTTPPort = useCallback(
    async (portNumber: number, options: OpenHTTPPortOptions) => {
      await client.openHTTPPort({
        externalNodeId: nodeId,
        portNumber,
        httpProtocol: options.httpProtocol,
        customHostname: buildSecureLinkCustomHostname(
          options.customHostname,
          portNumber,
          nodeId,
        ),
        authorizedEmails: options.authorizedEmails,
        allowPublicUnauthenticated: options.allowPublicUnauthenticated,
      });
      await fetchNode();
    },
    [nodeId, fetchNode],
  );

  const setPortTarget = useCallback(
    async (portId: string, portNumber: number) => {
      await client.setPortTarget({ portId, portNumber });
      await fetchNode();
    },
    [fetchNode],
  );

  const setPortAllowedSources = useCallback(
    async (portId: string, allowedSources: string[]) => {
      await client.setPortAllowedSources({ portId, allowedSources });
      await fetchNode();
    },
    [fetchNode],
  );

  const setHTTPPortProtocol = useCallback(
    async (portId: string, httpProtocol: HttpPortProtocol) => {
      await client.setHTTPPortProtocol({ portId, httpProtocol });
      await fetchNode();
    },
    [fetchNode],
  );

  const setHTTPPortAccess = useCallback(
    async (
      portId: string,
      authorizedEmails: string[],
      allowPublicUnauthenticated: boolean,
    ) => {
      await client.setHTTPPortAccess({
        portId,
        authorizedEmails,
        allowPublicUnauthenticated,
      });
      await fetchNode();
    },
    [fetchNode],
  );

  const closePort = useCallback(
    async (portId: string) => {
      await client.closePort({ portId });
      await fetchNode();
    },
    [fetchNode],
  );

  const grantSSHAccess = useCallback(
    async (userId: string, linuxUser: string) => {
      const portId = getSSHPortId(node?.ports ?? []);
      if (!portId) {
        throw new Error(
          "This node does not have an SSH port to grant access on.",
        );
      }

      await client.grantNodeSSHAccess({
        externalNodeId: nodeId,
        userId,
        linuxUser,
        portId,
      });
      await fetchNode();
    },
    [node, nodeId, fetchNode],
  );

  const revokeSSHAccess = useCallback(
    async (userId: string, linuxUser: string) => {
      const portId = getSSHPortId(node?.ports ?? []);
      if (!portId) {
        throw new Error(
          "This node does not have an SSH port to revoke access on.",
        );
      }

      await client.revokeNodeSSHAccess({
        externalNodeId: nodeId,
        userId,
        linuxUser,
        portId,
      });
      await fetchNode();
    },
    [node, nodeId, fetchNode],
  );

  const removeNode = useCallback(async () => {
    await client.removeNode({ externalNodeId: nodeId });
  }, [nodeId]);

  const setNetworkMemberType = useCallback(
    async (networkMemberType: NetworkMemberType) => {
      await client.setNetworkMemberType({
        externalNodeId: nodeId,
        networkMemberType,
      });
      await fetchNode();
    },
    [nodeId, fetchNode],
  );

  return {
    node,
    isLoading,
    fetchError,
    refetch: fetchNode,
    openPort,
    openHTTPPort,
    setPortTarget,
    setPortAllowedSources,
    setHTTPPortProtocol,
    setHTTPPortAccess,
    closePort,
    grantSSHAccess,
    revokeSSHAccess,
    removeNode,
    setNetworkMemberType,
  };
};
